<template>
  <div class="banner-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>首页轮播图管理</span>
          <el-button type="primary" @click="handleAdd">
            <el-icon><Plus /></el-icon>添加轮播图
          </el-button>
        </div>
      </template>

      <el-table :data="bannerList" v-loading="loading" style="width: 100%">
        <el-table-column type="index" width="50" />
        <el-table-column label="图片" width="200">
          <template #default="{ row }">
            <el-image 
              :src="row.image_url" 
              style="width: 180px; height: 80px; object-fit: cover"
              :preview-src-list="[row.image_url]"
            />
          </template>
        </el-table-column>
        <el-table-column prop="title" label="标题" width="150" />
        <el-table-column label="跳转类型" width="120">
          <template #default="{ row }">
            <el-tag :type="getJumpTypeTag(row.jump_type)">
              {{ getJumpTypeLabel(row.jump_type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="jump_target" label="跳转目标" min-width="200" show-overflow-tooltip />
        <el-table-column prop="sort_order" label="排序" width="80" />
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <el-switch
              v-model="row.status"
              :active-value="1"
              :inactive-value="0"
              @change="(val) => handleStatusChange(row, val)"
            />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleEdit(row)">编辑</el-button>
            <el-button type="danger" size="small" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-empty v-if="!loading && bannerList.length === 0" description="暂无轮播图数据" />
    </el-card>

    <!-- 添加/编辑对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="600px"
      destroy-on-close
    >
      <el-form :model="form" :rules="rules" ref="formRef" label-width="100px">
        <el-form-item label="轮播图" prop="image_url">
          <el-upload
            class="banner-uploader"
            :http-request="customUpload"
            :show-file-list="false"
            accept="image/*"
          >
            <el-image v-if="form.image_url" :src="form.image_url" class="upload-preview" />
            <el-icon v-else class="upload-icon"><Plus /></el-icon>
          </el-upload>
          <el-input v-model="form.image_url" placeholder="或输入图片URL" style="margin-top: 10px" />
        </el-form-item>

        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" placeholder="请输入轮播图标题" />
        </el-form-item>

        <el-form-item label="跳转类型" prop="jump_type">
          <el-radio-group v-model="form.jump_type">
            <el-radio-button value="page">页面跳转</el-radio-button>
            <el-radio-button value="web">网页链接</el-radio-button>
            <el-radio-button value="function">功能跳转</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="跳转目标" prop="jump_target">
          <el-select 
            v-if="form.jump_type === 'page'" 
            v-model="form.jump_target" 
            placeholder="选择小程序页面"
            style="width: 100%"
          >
            <el-option label="打印服务" value="/pages/print/print" />
            <el-option label="手抄报" value="/pages/handwriting/templates" />
            <el-option label="商城首页" value="/pages/shop/list/list" />
            <el-option label="会员中心" value="/pages/member/index/index" />
            <el-option label="个人中心" value="/pages/profile/index/index" />
          </el-select>
          <el-select 
            v-else-if="form.jump_type === 'function'" 
            v-model="form.jump_target" 
            placeholder="选择功能"
            style="width: 100%"
          >
            <el-option label="打印服务" value="print" />
            <el-option label="手抄报" value="hand-copy" />
            <el-option label="商城" value="shop" />
            <el-option label="会员中心" value="member" />
          </el-select>
          <el-input 
            v-else 
            v-model="form.jump_target" 
            placeholder="请输入网页URL，如 https://example.com"
          />
        </el-form-item>

        <el-form-item label="排序" prop="sort_order">
          <el-input-number v-model="form.sort_order" :min="0" :max="999" />
          <span class="form-tip">数字越小越靠前</span>
        </el-form-item>

        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :value="1">启用</el-radio>
            <el-radio :value="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import request from '../../utils/request'

export default {
  name: 'BannerList',
  setup() {
    const loading = ref(false)
    const bannerList = ref([])
    const dialogVisible = ref(false)
    const dialogTitle = ref('添加轮播图')
    const submitting = ref(false)
    const formRef = ref(null)
    const isEdit = ref(false)
    const editId = ref(null)

    const form = reactive({
      image_url: '',
      title: '',
      jump_type: 'page',
      jump_target: '',
      sort_order: 0,
      status: 1
    })

    const rules = {
      image_url: [{ required: true, message: '请上传轮播图', trigger: 'blur' }],
      jump_target: [{ required: true, message: '请输入跳转目标', trigger: 'blur' }]
    }

    const jumpTypeMap = {
      'page': { label: '页面跳转', tag: 'success' },
      'web': { label: '网页链接', tag: 'warning' },
      'function': { label: '功能跳转', tag: 'primary' }
    }

    const getJumpTypeLabel = (type) => jumpTypeMap[type]?.label || type
    const getJumpTypeTag = (type) => jumpTypeMap[type]?.tag || 'info'

    const loadBanners = async () => {
      loading.value = true
      try {
        const res = await request.get('/api/admin/banners')
        bannerList.value = res.data || []
      } catch (err) {
        console.error('Load banners error:', err)
      } finally {
        loading.value = false
      }
    }

    const handleAdd = () => {
      isEdit.value = false
      editId.value = null
      dialogTitle.value = '添加轮播图'
      Object.assign(form, {
        image_url: '',
        title: '',
        jump_type: 'page',
        jump_target: '',
        sort_order: 0,
        status: 1
      })
      dialogVisible.value = true
    }

    const handleEdit = (row) => {
      isEdit.value = true
      editId.value = row.id
      dialogTitle.value = '编辑轮播图'
      Object.assign(form, {
        image_url: row.image_url,
        title: row.title || '',
        jump_type: row.jump_type || 'page',
        jump_target: row.jump_target || row.link_url || '',
        sort_order: row.sort_order || 0,
        status: row.status
      })
      dialogVisible.value = true
    }

    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm('确定删除该轮播图吗？', '提示', { type: 'warning' })
        await request.delete(`/api/admin/banners/${row.id}`)
        ElMessage.success('删除成功')
        loadBanners()
      } catch (err) {
        if (err !== 'cancel') {
          console.error('Delete banner error:', err)
        }
      }
    }

    const handleStatusChange = async (row, val) => {
      try {
        await request.put(`/api/admin/banners/${row.id}`, { status: val })
        ElMessage.success('状态更新成功')
      } catch (err) {
        row.status = val === 1 ? 0 : 1
        console.error('Update status error:', err)
      }
    }

    const customUpload = async (options) => {
      const formData = new FormData()
      formData.append('file', options.file)
      try {
        const res = await request.post('/api/admin/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        if (res.code === 0) {
          form.image_url = res.data.url
          ElMessage.success('上传成功')
        }
      } catch (err) {
        ElMessage.error('上传失败')
        console.error('Upload error:', err)
      }
    }

    const handleSubmit = async () => {
      const valid = await formRef.value.validate().catch(() => false)
      if (!valid) return

      submitting.value = true
      try {
        const data = {
          image: form.image_url,
          image_url: form.image_url,
          title: form.title,
          jump_type: form.jump_type,
          jump_target: form.jump_target,
          link_url: form.jump_target,
          link_type: form.jump_type,
          sort_order: form.sort_order,
          status: form.status
        }

        if (isEdit.value) {
          await request.put(`/api/admin/banners/${editId.value}`, data)
          ElMessage.success('更新成功')
        } else {
          await request.post('/api/admin/banners', data)
          ElMessage.success('添加成功')
        }
        dialogVisible.value = false
        loadBanners()
      } catch (err) {
        console.error('Submit banner error:', err)
      } finally {
        submitting.value = false
      }
    }

    onMounted(() => {
      loadBanners()
    })

    return {
      loading,
      bannerList,
      dialogVisible,
      dialogTitle,
      submitting,
      formRef,
      form,
      rules,
      getJumpTypeLabel,
      getJumpTypeTag,
      handleAdd,
      handleEdit,
      handleDelete,
      handleStatusChange,
      customUpload,
      handleSubmit
    }
  }
}
</script>

<style scoped>
.banner-management {
  padding: 8px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}

.card-header span {
  font-weight: 600;
  font-size: 16px;
  color: #303133;
}

.banner-uploader {
  border: 2px dashed #d9d9d9;
  border-radius: 12px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 360px;
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  background: #fafbfc;
}

.banner-uploader:hover {
  border-color: #409EFF;
  background: rgba(64, 158, 255, 0.02);
}

.upload-preview {
  width: 360px;
  height: 160px;
  object-fit: cover;
  border-radius: 12px;
}

.upload-icon {
  font-size: 32px;
  color: #c0c4cc;
  transition: all 0.3s ease;
}

.banner-uploader:hover .upload-icon {
  color: #409EFF;
  transform: scale(1.1);
}

.form-tip {
  margin-left: 10px;
  color: #909399;
  font-size: 12px;
}

/* 表格图片 */
:deep(.el-image) {
  border-radius: 8px;
  overflow: hidden;
}
</style>
